import { supabase } from '../client';
import { ShippingInfo } from '@/components/ShippingInfoForm';
import { logError } from '@/lib/sentry';
import { isEmptyFieldValue } from '@/lib/utils';
import { runVerifiedKeysetScan } from '@/utils/keysetScan';
import { validateShippingSyncResponse } from '@/utils/shippingSyncResponse';

// DB 테이블 스키마와 유사한 인터페이스
export interface ParsedOrderRecord {
  id: number;
  user_id: string;
  order_date: string;
  nickname: string;
  name: string;
  phone_number: string;
  address: string;
  delivery_notes: string;
  product_name: string;
  order_id?: number | null;
  row_order?: number | null;
  created_at?: string;
}

/**
 * 동일 user_id + order_date 내에서 다음 row_order 값을 계산합니다.
 * - 배송엑셀(NewExcel)과 동일하게 "마지막 + 1"로 부여하여 표시 순서를 안정화합니다.
 */
const getNextRowOrder = async (userId: string, orderDate: string): Promise<number> => {
  const { data, error } = await supabase
    .from('parsed_order')
    .select('row_order')
    .eq('user_id', userId)
    .eq('order_date', orderDate)
    .order('row_order', { ascending: false })
    .limit(1);

  if (error) {
    logError('Error getting next row_order:', error);
    throw error;
  }

  const max = data?.[0]?.row_order;
  const maxNum = typeof max === 'number' ? max : -1;
  return maxNum + 1;
};

export const findByOrderId = async (orderId: number): Promise<ParsedOrderRecord | null> => {
  const { data, error } = await supabase
    .from('parsed_order')
    .select('*')
    .eq('order_id', orderId)
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') {
    logError('Error finding parsed order by order_id:', error);
    throw error;
  }
  return data;
};

export const findByCustomer = async (
  userId: string,
  orderDate: string,
  nickname: string
): Promise<ParsedOrderRecord | null> => {
  const { data, error } = await supabase
    .from('parsed_order')
    .select('*')
    .eq('user_id', userId)
    .eq('order_date', orderDate)
    .eq('nickname', nickname)
    .limit(1)
    .single();

  if (error && error.code !== 'PGRST116') { // PGRST116: 'No rows found'
    logError('Error finding parsed order:', error);
    throw error;
  }

  return data;
};

export const findLatestByUserAndNickname = async (
  userId: string,
  nickname: string
): Promise<ParsedOrderRecord | null> => {
  const { data, error } = await supabase
    .from('parsed_order')
    .select('*')
    .eq('user_id', userId)
    .eq('nickname', nickname)
    .order('order_date', { ascending: false })
    .order('created_at', { ascending: false })
    .limit(1);

  if (error && error.code !== 'PGRST116') {
    logError('Error finding latest parsed order by nickname:', error);
    throw error;
  }

  return data?.[0] ?? null;
};

export const upsert = async (
  userId: string,
  orderDate: string,
  shippingInfo: ShippingInfo
): Promise<ParsedOrderRecord> => {
  // 1. 먼저 기존 데이터가 있는지 확인합니다.
  const existingRecord = await findByCustomer(userId, orderDate, shippingInfo.nickname);

  const recordData = {
    user_id: userId,
    order_date: orderDate,
    nickname: shippingInfo.nickname,
    name: shippingInfo.customer_name,
    phone_number: shippingInfo.customer_phone,
    address: shippingInfo.customer_address,
    delivery_notes: shippingInfo.delivery_notes,
    product_name: shippingInfo.productName,
  };

  if (existingRecord) {
    // 2a. 데이터가 있으면 UPDATE
    const { data, error } = await supabase
      .from('parsed_order')
      .update(recordData)
      .eq('id', existingRecord.id)
      .select()
      .single();
    
    if (error) {
      logError('Error updating parsed order:', error);
      throw error;
    }
    return data;
  } else {
    // 2b. 데이터가 없으면 INSERT
    const { data, error } = await supabase
      .from('parsed_order')
      .insert(recordData)
      .select()
      .single();

    if (error) {
      logError('Error inserting parsed order:', error);
      throw error;
    }
    return data;
  }
};

export const upsertByOrderId = async (
  orderId: number,
  userId: string,
  orderDate: string,
  shippingInfo: ShippingInfo
): Promise<ParsedOrderRecord> => {
  const recordData: Record<string, any> = {
    user_id: userId,
    order_date: orderDate,
    nickname: shippingInfo.nickname,
    name: shippingInfo.customer_name,
    phone_number: shippingInfo.customer_phone,
    address: shippingInfo.customer_address,
    delivery_notes: shippingInfo.delivery_notes,
    product_name: shippingInfo.productName,
    order_id: orderId,
  };

  // order_id를 기준으로 upsert를 시도합니다.
  // 이를 위해서는 DB에 order_id에 대한 UNIQUE 제약조건이 필요합니다.
  // 제약조건이 없다면, 아래 로직은 find + update/insert로 변경해야 합니다.
  const existingRecord = await findByOrderId(orderId);

  if (existingRecord) {
    // clobber 방어: 빈 productName으로 기존 DB product_name을 덮지 않는다.
    // (stale/빈 items에서 파생된 '' 저장 시 멀쩡한 값이 사라지던 문제)
    // 정상(non-empty) 저장엔 영향 없음. INSERT는 덮을 기존값이 없으므로 무변경.
    if (isEmptyFieldValue(shippingInfo.productName)) {
      delete recordData.product_name;
    }
    // 데이터가 있으면 UPDATE
    const { data, error } = await supabase
      .from('parsed_order')
      .update(recordData)
      .eq('id', existingRecord.id)
      .select()
      .single();
    
    if (error) {
      logError('Error updating parsed order by order_id:', error);
      throw error;
    }
    return data;
  } else {
    // 데이터가 없으면 INSERT (row_order는 NewExcel과 동일하게 마지막+1로 부여)
    recordData.row_order = await getNextRowOrder(userId, orderDate);

    // 데이터가 없으면 INSERT
    const { data, error } = await supabase
      .from('parsed_order')
      .insert(recordData)
      .select()
      .single();

    if (error) {
      logError('Error inserting parsed order with order_id:', error);
      throw error;
    }
    return data;
  }
};

// 여러 order_id로 배송 정보 일괄 조회.
// complete=false = 일부 청크가 실패했거나 응답이 잘렸을 수 있음 — 이때 "응답에 없음"을
// "행이 없음"으로 단정하면 안 된다(음성 캐시의 false-negative 방지, no-await-open 플랜 §A).
export interface FindByOrderIdsResult {
  rows: ParsedOrderRecord[];
  complete: boolean;
}

// URL 길이·PostgREST max_rows 절단을 피하는 안전 청크 크기.
const FIND_BY_ORDER_IDS_CHUNK = 200;

export const findByOrderIds = async (orderIds: number[]): Promise<FindByOrderIdsResult> => {
  if (!orderIds || orderIds.length === 0) return { rows: [], complete: true };

  const chunks: number[][] = [];
  for (let i = 0; i < orderIds.length; i += FIND_BY_ORDER_IDS_CHUNK) {
    chunks.push(orderIds.slice(i, i + FIND_BY_ORDER_IDS_CHUNK));
  }

  // per-chunk settle: 한 청크의 실패가 성공한 청크의 양성까지 버리지 않는다.
  const settled = await Promise.allSettled(
    chunks.map(async (chunk) => {
      const { data, count, error } = await supabase
        .from('parsed_order')
        .select('*', { count: 'exact' })
        .in('order_id', chunk);
      if (error) throw error;
      const rows = data || [];
      // 완전 = count 비-null 동등. count가 null이거나 rows보다 크면(절단) 미완전.
      return { rows, complete: count !== null && count === rows.length };
    })
  );

  const rows: ParsedOrderRecord[] = [];
  let complete = true;
  for (const s of settled) {
    if (s.status === 'fulfilled') {
      rows.push(...s.value.rows);
      if (!s.value.complete) complete = false;
    } else {
      logError('Error finding parsed orders by order_ids:', s.reason);
      complete = false;
    }
  }
  return { rows, complete };
};

// 새로 추가: order_id로 parsed_order의 order_date 업데이트 (단건)
export const patchOrderDateByOrderId = async (orderId: number, orderDate: string): Promise<void> => {
  const { error } = await supabase
    .from('parsed_order')
    .update({ order_date: orderDate })
    .eq('order_id', orderId);

  if (error) {
    logError('Error updating parsed_order.order_date by order_id:', error);
    throw error;
  }
};

// 새로 추가: 여러 order_id에 대해 parsed_order의 order_date 일괄 업데이트
// affected row count 반환 (UI에서 "함께 변경" 부가 문구 분기용)
export const patchOrderDateByOrderIds = async (orderIds: number[], orderDate: string): Promise<number> => {
  if (!orderIds || orderIds.length === 0) return 0;
  const { data, error } = await supabase
    .from('parsed_order')
    .update({ order_date: orderDate })
    .in('order_id', orderIds)
    .select('id');

  if (error) {
    logError('Error bulk updating parsed_order.order_date by order_ids:', error);
    throw error;
  }
  return data?.length ?? 0;
};

// 특정 시각 이후 생성된 자동정리(parsed_order) 이력 존재 여부만 확인 (경량 판별용 — 출시 팝업 타겟팅 등).
// order_date는 수정 가능한 영업일자라 활동 판별엔 created_at을 쓴다.
export const existsParsedOrderSince = async (userId: string, sinceIso: string): Promise<boolean> => {
  const { data, error } = await supabase
    .from('parsed_order')
    .select('id')
    .eq('user_id', userId)
    .gte('created_at', sinceIso)
    .limit(1);

  if (error) {
    logError('Error checking parsed_order existence:', error);
    throw error;
  }
  return (data?.length ?? 0) > 0;
};

// 새로 추가: 배송정보 상품명(product_name)만 부분 갱신.
// upsertByOrderId는 전체 필드를 쓰므로 부분 갱신에 부적합 — 다른 필드를 건드리지 않는 전용 patch.
// select('id')로 실제 갱신 행을 확인 — 검사~클릭 사이 행이 삭제된 경우 조용한 가짜 성공을 막는다.
export const patchProductNameByOrderId = async (orderId: number, productName: string): Promise<void> => {
  const { data, error } = await supabase
    .from('parsed_order')
    .update({ product_name: productName })
    .eq('order_id', orderId)
    .select('id');

  if (error) {
    logError('Error patching product_name by order_id:', error);
    throw error;
  }
  if (!data || data.length === 0) {
    throw new Error(`parsed_order row not found for order_id ${orderId}`);
  }
};

// ── 배송정보 상품명 stale 일괄 반영 (플랜 codexloop 합의, 2026-07-29) ─────────────

// 스캔 후보 행 — 미리보기 CAS의 기준값이 되는 필드만 최소로 가져온다.
// modified_at은 문자열 그대로 보존해 RPC에 되돌려준다(Date 파싱/재직렬화 금지 —
// 마이크로초 절사가 conflict_stored 오탐을 만든다).
export interface ShippingSyncCandidateRow {
  id: number;
  order_id: number;
  /** 구매자 닉네임 — 미리보기 모달 표시용 */
  nickname: string | null;
  product_name: string | null;
  // 스키마에 NOT NULL 제약이 없다(프로드 143,702행 중 null 0건이지만 미보장) —
  // null이면 CAS 기준값이 없으므로 apply 후보에서 제외해야 한다(fail-closed).
  modified_at: string | null;
}

/**
 * 선택 날짜의 stale 스캔 후보(parsed_order) 조회 — keyset + 무결성 계약.
 * orderItemRepository.findByOrderIdsForSyncScan과 동일 계약(baseline/final count 동등,
 * id 유일·단조, distinct === count). verified=false = 스캔 실패로 처리할 것.
 * 페이지 rows state에서 파생하지 않는 독립 조회다(CustomerRow에는 order_id가 없음).
 */
export const findShippingSyncCandidatesByDate = async (
  userId: string,
  orderDate: string
): Promise<{ rows: ShippingSyncCandidateRow[]; verified: boolean }> => {
  return runVerifiedKeysetScan<ShippingSyncCandidateRow>(
    {
      fetchCount: async () => {
        const { count, error } = await supabase
          .from('parsed_order')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', userId)
          .eq('order_date', orderDate)
          .not('order_id', 'is', null);
        if (error) throw error;
        return count;
      },
      fetchPage: async (afterId, limit) => {
        const { data, error } = await supabase
          .from('parsed_order')
          .select('id, order_id, nickname, product_name, modified_at')
          .eq('user_id', userId)
          .eq('order_date', orderDate)
          .not('order_id', 'is', null)
          .gt('id', afterId)
          .order('id', { ascending: true })
          .limit(limit);
        if (error) throw error;
        return (data ?? []) as ShippingSyncCandidateRow[];
      },
    },
    {
      pageSize: 500,
      getId: (row) => row.id,
      isRowExpected: (row) => typeof row.order_id === 'number',
    }
  );
};

// RPC 페이로드 계약 — supabase/migrations/20260729110000_shipping_product_name_sync_rpc_v2.sql 참조.
export interface ShippingSyncApplyRow {
  parsedOrderId: number;
  expectedOrderId: number;
  expectedStored: string | null;
  /** 후보 조회의 modified_at 문자열 원본 그대로 (재직렬화 금지 — µs 절사가 conflict 오탐) */
  expectedModifiedAt: string;
  /** 원시 order_item 전 행(빈 이름 포함), id ASC, 정확히 {id, item_name, quantity} 3키 */
  expectedItems: { id: number; item_name: string | null; quantity: number | null }[];
  newValue: string;
}

export type { ShippingSyncApplyStatus, ShippingSyncApplyResult } from '@/utils/shippingSyncResponse';

/**
 * 배치 CAS 반영 RPC 호출. JSONB envelope로 받아 "제출 id 전부에 허용 상태 1개씩"을 검증한다
 * (RETURNS TABLE은 PostgREST 행 cap에 잘릴 수 있음 — v2에서 envelope로 전환).
 * 예외 = 페이로드 자체 거부(비배열/500행 초과/중복 id)·네트워크·envelope 검증 실패.
 * 전부 commit-ambiguous로 취급 — 호출부는 블라인드 재시도 대신 재스캔할 것.
 */
export const applyShippingProductNameSync = async (
  rows: ShippingSyncApplyRow[]
): Promise<import('@/utils/shippingSyncResponse').ShippingSyncApplyResult[]> => {
  const { data, error } = await supabase.rpc('apply_shipping_product_name_sync', {
    p_rows: rows,
  });
  if (error) {
    logError('Error applying shipping product name sync:', error);
    throw error;
  }
  return validateShippingSyncResponse(rows.map((r) => r.parsedOrderId), data);
};
